import * as React from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Link, MenuItem, Stack, Switch, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    AI_LABELS, createAiWorkflow, deleteAiWorkflow, exportAiBundle, getAiAgents, getAiTasks, getAiWorkflow, getAiWorkflowStats,
    getAiWorkflows, updateAiWorkflow,
} from '../../Api';
import {
    AiStatus, AiUpsertWorkflowRequest, AiWorkflowDto, AiWorkflowMemberInfoDto, AiWorkflowMemberType, AiWorkflowStatsDto,
} from '../../models/dtos/AiDto';
import { ConfirmDialog } from '../../components/betupFeed/BetupFeedShared';
import {
    AiActionError, AiErrorAlert, aiLoadErrorText, AiStatusChip, formatAiCount, formatAiDate, formatAiUsd, KpiCard, textError,
    toAiActionError,
} from '../../components/ai/AiShared';

/** Downloads a bundle of the workflow with its member agents and tasks (import it on another environment). */
async function downloadWorkflowBundle(code: string): Promise<string | null> {
    const result = await exportAiBundle({ workflows: [code], includeDependencies: true });
    if (result.error != null || result.data == null) return result.error ?? 'Export failed';
    const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-workflow-${code}.json`;
    a.click();
    URL.revokeObjectURL(url);
    return null;
}

/** A member shared with other workflows reads "(secondary)" where this isn't its primary workflow. */
function MemberChips({ members, max = 6 }: { members: AiWorkflowMemberInfoDto[]; max?: number }) {
    return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
            {members.slice(0, max).map(m => (
                <Tooltip key={`${m.type}:${m.code}`} title={m.alsoIn.length ? `Also in ${m.alsoIn.join(', ')}` : ''}>
                    <Chip size="small" variant="outlined"
                        label={`${m.type === 'AGENT' ? 'agent' : 'task'} ${m.code}${m.primary ? '' : ' (secondary)'}`}
                        component={RouterLink} clickable to={`/ai/${m.type === 'AGENT' ? 'agents' : 'tasks'}/${encodeURIComponent(m.code)}`} />
                </Tooltip>
            ))}
            {members.length > max && <Chip size="small" label={`+${members.length - max}`} />}
        </Stack>
    );
}

/**
 * AI › Workflows: named groups of agents and tasks (e.g. every AI step of a match brief), reported and capped together.
 * Membership is edited here; budgets with a workflow scope are on the Budgets page.
 */
export default function AiWorkflowsPage() {
    const navigate = useNavigate();
    const [rows, setRows] = React.useState<AiWorkflowDto[] | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        const list = await getAiWorkflows();
        setLoading(false);
        setRows(list);
        setError(list == null ? aiLoadErrorText(AI_LABELS.workflows, 'Failed to load workflows.') : null);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const sorted = (rows ?? []).slice().sort((a, b) => b.cost30dUsd - a.cost30dUsd);
    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4">Workflows</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Groups of agents and tasks behind one product flow. A call is attributed to the workflow its request
                        names (the agent or task must belong to it), else to the member's primary workflow, so spend is
                        reported per workflow and per instance (run key), and a workflow budget caps them together. An agent
                        or task may belong to several workflows; one of them is its primary.
                    </Typography>
                </Box>
                <Stack direction="row" gap={1}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/ai/workflows/new')}>New workflow</Button>
                </Stack>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            {loading && rows == null && <CircularProgress size={24} />}
            {rows && rows.length === 0 && <Typography color="text.secondary">No workflows yet.</Typography>}
            {sorted.length > 0 && (
                <Card variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Workflow</TableCell>
                                <TableCell>Members</TableCell>
                                <TableCell align="right">Today</TableCell>
                                <TableCell align="right">30 days</TableCell>
                                <TableCell align="right">Runs (30 days)</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map(w => (
                                <TableRow key={w.code} hover sx={{ cursor: 'pointer', verticalAlign: 'top' }}
                                    onClick={() => navigate(`/ai/workflows/${encodeURIComponent(w.code)}`)}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{w.name}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{w.code}</Typography>
                                    </TableCell>
                                    <TableCell onClick={e => e.stopPropagation()}><MemberChips members={w.members} /></TableCell>
                                    <TableCell align="right">{formatAiUsd(w.costTodayUsd)}</TableCell>
                                    <TableCell align="right">{formatAiUsd(w.cost30dUsd)}</TableCell>
                                    <TableCell align="right">{formatAiCount(w.runs30d)}</TableCell>
                                    <TableCell><AiStatusChip status={w.status} /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </Stack>
    );
}

interface WorkflowForm {
    code: string;
    name: string;
    description: string;
    status: AiStatus;
    agents: string[];
    tasks: string[];
    /** member key (AGENT:code / TASK:code) → this is its primary workflow. */
    primary: Record<string, boolean>;
}

const EMPTY_FORM: WorkflowForm = { code: '', name: '', description: '', status: 'ACTIVE', agents: [], tasks: [], primary: {} };

const memberKey = (type: AiWorkflowMemberType, code: string) => `${type}:${code}`;

/** A workflow a member belongs to (from the workflow list). */
interface Membership { workflow: string; primary: boolean }

/** One workflow: name, members (agents and tasks), and its report — spend by day, member, model and instance. */
export function AiWorkflowEditPage() {
    const { code: codeParam } = useParams<{ code: string }>();
    const isNew = codeParam === 'new';
    const navigate = useNavigate();
    const [form, setForm] = React.useState<WorkflowForm>(EMPTY_FORM);
    const [workflow, setWorkflow] = React.useState<AiWorkflowDto | null>(null);
    const [agentCodes, setAgentCodes] = React.useState<string[]>([]);
    const [taskCodes, setTaskCodes] = React.useState<string[]>([]);
    /** member key (AGENT:code / TASK:code) → every workflow it belongs to now. */
    const [memberships, setMemberships] = React.useState<Map<string, Membership[]>>(new Map());
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [actionError, setActionError] = React.useState<AiActionError | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    const [days, setDays] = React.useState(30);
    const [stats, setStats] = React.useState<AiWorkflowStatsDto | null>(null);
    const [statsLoading, setStatsLoading] = React.useState(false);

    const load = React.useCallback(async () => {
        const [tasks, agents, all] = await Promise.all([getAiTasks({}, 0, 200), getAiAgents({}, 0, 200), getAiWorkflows()]);
        setTaskCodes((tasks?.items ?? []).map(t => t.code).sort());
        setAgentCodes((agents?.items ?? []).map(a => a.code).sort());
        const byMember = new Map<string, Membership[]>();
        (all ?? []).forEach(w => w.members.forEach(m => {
            const key = memberKey(m.type, m.code);
            byMember.set(key, [...(byMember.get(key) ?? []), { workflow: w.code, primary: m.primary }]);
        }));
        setMemberships(byMember);
        if (isNew || !codeParam) return;
        const w = await getAiWorkflow(codeParam);
        if (w == null) return setLoadError(aiLoadErrorText(AI_LABELS.workflow, 'Failed to load the workflow.'));
        setWorkflow(w);
        setForm({
            code: w.code, name: w.name, description: w.description ?? '', status: w.status,
            agents: w.members.filter(m => m.type === 'AGENT').map(m => m.code),
            tasks: w.members.filter(m => m.type === 'TASK').map(m => m.code),
            primary: Object.fromEntries(w.members.map(m => [memberKey(m.type, m.code), m.primary])),
        });
    }, [codeParam, isNew]);
    React.useEffect(() => { load(); }, [load]);

    React.useEffect(() => {
        if (isNew || !codeParam) return;
        setStatsLoading(true);
        getAiWorkflowStats(codeParam, days).then(s => { setStats(s); setStatsLoading(false); });
    }, [codeParam, isNew, days]);

    const patch = (p: Partial<WorkflowForm>) => setForm(f => ({ ...f, ...p }));
    /** The member's other workflows (this one left out). */
    const elsewhere = (type: AiWorkflowMemberType, code: string) =>
        (memberships.get(memberKey(type, code)) ?? []).filter(m => m.workflow !== codeParam);
    const memberLabel = (type: AiWorkflowMemberType) => (code: string) => {
        const others = elsewhere(type, code).map(m => m.workflow);
        return others.length ? `${code}  (also in ${others.join(', ')})` : code;
    };
    /** New members become primary here unless they already have a primary workflow. */
    const setMembers = (type: AiWorkflowMemberType, codes: string[]) => setForm(f => {
        const primary = { ...f.primary };
        codes.forEach(code => {
            const key = memberKey(type, code);
            if (!(key in primary)) primary[key] = !elsewhere(type, code).some(m => m.primary);
        });
        return { ...f, ...(type === 'AGENT' ? { agents: codes } : { tasks: codes }), primary };
    });
    const memberRows = [
        ...form.agents.map(code => ({ type: 'AGENT' as const, code })),
        ...form.tasks.map(code => ({ type: 'TASK' as const, code })),
    ];

    const save = async () => {
        if (!form.name.trim()) return setActionError(textError('Name is required.'));
        if (isNew && !/^[a-z0-9][a-z0-9._-]{1,127}$/.test(form.code.trim())) {
            return setActionError(textError("Code: 2–128 lower case letters, digits, '.', '-' or '_'."));
        }
        const request: AiUpsertWorkflowRequest = {
            code: isNew ? form.code.trim() : null,
            name: form.name.trim(),
            description: form.description.trim() || null,
            status: form.status,
            members: memberRows.map(m => ({ ...m, primary: form.primary[memberKey(m.type, m.code)] ?? null })),
        };
        setSaving(true);
        const result = isNew ? await createAiWorkflow(request) : await updateAiWorkflow(codeParam!, request);
        setSaving(false);
        if (result.error != null || result.data == null) return setActionError(toAiActionError(result, 'Save failed'));
        setActionError(null);
        if (isNew) navigate(`/ai/workflows/${encodeURIComponent(result.data.code)}`, { replace: true });
        else load();
    };

    const remove = async () => {
        setConfirmDelete(false);
        const result = await deleteAiWorkflow(codeParam!);
        if (result.error != null) return setActionError(toAiActionError(result, 'Delete failed'));
        navigate('/ai/workflows');
    };

    const exportBundle = async () => {
        const error = await downloadWorkflowBundle(codeParam!);
        if (error) setActionError(textError(error));
    };

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4">{isNew ? 'New workflow' : (workflow?.name ?? codeParam)}</Typography>
                    {!isNew && <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{codeParam}</Typography>}
                </Box>
                <Stack direction="row" gap={1}>
                    <Button component={RouterLink} to="/ai/workflows">All workflows</Button>
                    {!isNew && <Button component={RouterLink} to="/ai/budgets">Budgets</Button>}
                    {!isNew && <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportBundle}>Export JSON</Button>}
                    {!isNew && <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>Delete</Button>}
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving}>Save</Button>
                </Stack>
            </Stack>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            <AiErrorAlert error={actionError} onClose={() => setActionError(null)} />

            <Card variant="outlined">
                <CardContent>
                    <Grid container spacing={2}>
                        {isNew && (
                            <Grid item xs={12} md={4}>
                                <TextField label="Code" fullWidth required value={form.code} onChange={e => patch({ code: e.target.value })}
                                    helperText="e.g. match.brief — what callers pass as workflow, and what reports show." />
                            </Grid>
                        )}
                        <Grid item xs={12} md={isNew ? 5 : 9}>
                            <TextField label="Name" fullWidth required value={form.name} onChange={e => patch({ name: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField select label="Status" fullWidth value={form.status} onChange={e => patch({ status: e.target.value as AiStatus })}>
                                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                <MenuItem value="DISABLED">DISABLED</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Description" fullWidth multiline minRows={2} value={form.description}
                                onChange={e => patch({ description: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete multiple options={agentCodes} value={form.agents} getOptionLabel={memberLabel('AGENT')}
                                onChange={(_, v) => setMembers('AGENT', v)}
                                renderInput={params => <TextField {...params} label="Agents" helperText="Their runs, child runs and the tasks they call count here when the request names this workflow (or none, where it's their primary)." />} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete multiple options={taskCodes} value={form.tasks} getOptionLabel={memberLabel('TASK')}
                                onChange={(_, v) => setMembers('TASK', v)}
                                renderInput={params => <TextField {...params} label="Tasks" helperText="Their executions by services count here when the request names this workflow (or none, where it's their primary)." />} />
                        </Grid>
                        {memberRows.length > 0 && (
                            <Grid item xs={12}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Member</TableCell>
                                            <TableCell>Primary here</TableCell>
                                            <TableCell>Also in</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {memberRows.map(m => {
                                            const key = memberKey(m.type, m.code);
                                            const others = elsewhere(m.type, m.code);
                                            const primaryElsewhere = others.find(o => o.primary)?.workflow;
                                            const isPrimary = form.primary[key] ?? false;
                                            return (
                                                <TableRow key={key}>
                                                    <TableCell sx={{ fontFamily: 'monospace' }}>{m.type === 'AGENT' ? 'agent' : 'task'} {m.code}</TableCell>
                                                    <TableCell>
                                                        <Switch size="small" checked={isPrimary}
                                                            onChange={e => patch({ primary: { ...form.primary, [key]: e.target.checked } })} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {isPrimary
                                                                ? (primaryElsewhere ? `moves its primary from ${primaryElsewhere}` : 'calls naming no workflow come here')
                                                                : (primaryElsewhere ? `primary: ${primaryElsewhere}` : 'no primary: calls naming no workflow are unattributed')}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {others.length === 0 ? '—' : (
                                                            <Stack direction="row" gap={0.5} flexWrap="wrap">
                                                                {others.map(o => (
                                                                    <Chip key={o.workflow} size="small" variant="outlined" clickable component={RouterLink}
                                                                        to={`/ai/workflows/${encodeURIComponent(o.workflow)}`}
                                                                        label={`${o.workflow}${o.primary ? ' (primary)' : ''}`} />
                                                                ))}
                                                            </Stack>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                <Typography variant="caption" color="text.secondary">
                                    A member may belong to several workflows; its primary one receives the calls whose request names
                                    no workflow. Removing a member here takes it out of this workflow only.
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            {!isNew && (
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                        <Typography variant="h5">Report</Typography>
                        <TextField select size="small" label="Period" value={days} onChange={e => setDays(Number(e.target.value))} sx={{ width: 160 }}>
                            {[7, 30, 90].map(d => <MenuItem key={d} value={d}>Last {d} days</MenuItem>)}
                        </TextField>
                    </Stack>
                    {statsLoading && stats == null && <CircularProgress size={24} />}
                    {stats && <WorkflowReport stats={stats} />}
                </Box>
            )}
            <ConfirmDialog open={confirmDelete} title={`Delete workflow ${codeParam}?`}
                text="Its agents and tasks leave it (their other workflows stay; one whose primary it was has none until you pick one); past usage keeps its attribution. Budgets scoped to it must be deleted first."
                confirmLabel="Delete" confirmColor="error" onClose={() => setConfirmDelete(false)} onConfirm={remove} />
        </Stack>
    );
}

function WorkflowReport({ stats }: { stats: AiWorkflowStatsDto }) {
    const days = stats.daily.map(d => d.day);
    const members = stats.members.slice(0, 12);
    const models = stats.models.slice(0, 8);
    return (
        <Stack gap={2}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={3}><KpiCard title="Cost" value={formatAiUsd(stats.costUsd)} lines={[['Calls', formatAiCount(stats.calls)], ['Errors', formatAiCount(stats.errors)], ['Blocked', formatAiCount(stats.blocked)]]} /></Grid>
                <Grid item xs={12} md={3}><KpiCard title="Runs" value={formatAiCount(stats.runs)} lines={[['Average per run', formatAiUsd(stats.avgRunCostUsd)], ['Most expensive run', formatAiUsd(stats.maxRunCostUsd)]]} /></Grid>
                <Grid item xs={12} md={3}><KpiCard title="Web searches" value={formatAiCount(stats.webSearches)} lines={[['Search fees', formatAiUsd(stats.webSearches * 0.01)]]} /></Grid>
                <Grid item xs={12} md={3}><KpiCard title="Per day" value={formatAiUsd(stats.daily.length ? stats.costUsd / stats.days : 0)} lines={[['Days with usage', formatAiCount(stats.daily.length)]]} /></Grid>
            </Grid>
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">Daily cost and runs</Typography>
                    {days.length === 0 ? <Typography variant="body2" color="text.secondary">No usage in this period.</Typography> : (
                        <LineChart
                            xAxis={[{ scaleType: 'point', data: days, valueFormatter: (d: string) => d.slice(5) }]}
                            yAxis={[{ id: 'cost', valueFormatter: (v: number) => formatAiUsd(v) }, { id: 'runs' }]}
                            leftAxis="cost"
                            rightAxis="runs"
                            series={[
                                { data: stats.daily.map(d => d.costUsd), label: 'Cost (USD)', yAxisKey: 'cost', area: true, showMark: false, valueFormatter: (v: number | null) => formatAiUsd(v) },
                                { data: stats.daily.map(d => d.runs), label: 'Runs', yAxisKey: 'runs', showMark: false },
                            ]}
                            height={280}
                            margin={{ left: 80, right: 60 }}
                        />
                    )}
                </CardContent>
            </Card>
            <Grid container spacing={2}>
                {[{ title: 'Cost by member', rows: members }, { title: 'Cost by model', rows: models }].map(({ title, rows }) => (
                    <Grid item xs={12} md={6} key={title}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
                                {rows.length === 0 ? <Typography variant="body2" color="text.secondary">No usage in this period.</Typography> : (
                                    <BarChart
                                        layout="horizontal"
                                        yAxis={[{ scaleType: 'band', data: rows.map(r => r.code) }]}
                                        xAxis={[{ valueFormatter: (v: number) => formatAiUsd(v) }]}
                                        series={[{ data: rows.map(r => r.costUsd), label: 'Cost (USD)', valueFormatter: (v: number | null) => formatAiUsd(v) }]}
                                        height={Math.max(160, 40 + rows.length * 32)}
                                        margin={{ left: Math.min(300, 20 + Math.max(...rows.map(r => r.code.length)) * 7), right: 40, top: 20, bottom: 30 }}
                                        slotProps={{ legend: { hidden: true } }}
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">Recent runs</Typography>
                    <Typography variant="caption" color="text.secondary">
                        One row per instance (the caller's run key or correlation id, e.g. match-brief:12): what one brief, one
                        generation… cost. Open one to see every call it made.
                    </Typography>
                    {stats.recentRuns.length === 0 ? <Typography variant="body2" color="text.secondary">No runs with a run key in this period.</Typography> : (
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Run key</TableCell>
                                    <TableCell align="right">Cost</TableCell>
                                    <TableCell align="right">Calls</TableCell>
                                    <TableCell align="right">Errors</TableCell>
                                    <TableCell align="right">Web searches</TableCell>
                                    <TableCell>First call</TableCell>
                                    <TableCell>Last call</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.recentRuns.map(r => (
                                    <TableRow key={r.runKey}>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>
                                            <Link component={RouterLink} to={`/ai/workflows/${encodeURIComponent(stats.code)}/runs/${encodeURIComponent(r.runKey)}`}>{r.runKey}</Link>
                                        </TableCell>
                                        <TableCell align="right">{formatAiUsd(r.costUsd)}</TableCell>
                                        <TableCell align="right">{formatAiCount(r.calls)}</TableCell>
                                        <TableCell align="right">{formatAiCount(r.errors)}</TableCell>
                                        <TableCell align="right">{formatAiCount(r.webSearches)}</TableCell>
                                        <TableCell>{formatAiDate(r.firstAt)}</TableCell>
                                        <TableCell>{formatAiDate(r.lastAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Stack>
    );
}
